import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, Heart } from 'lucide-react';
import { Product } from '@/types';
import { formatCurrency, getDiscountPercent, cn } from '@/utils';
import { RatingStars } from '@/components/common/RatingStars';
import { useCartStore } from '@/store/cartStore';
import { useWishlistStore } from '@/store/wishlistStore';
import { useAuthStore } from '@/store/authStore';
import { cartService } from '@/services/cart.service';
import { api } from '@/services';
import toast from '@/hooks/useToast';

interface ProductCardProps {
  product: Product;
  className?: string;
}

export const ProductCard = ({ product, className }: ProductCardProps) => {
  const [addingToCart, setAddingToCart] = useState(false);
  const { setCart } = useCartStore();
  const { isInWishlist, addItem, removeItem } = useWishlistStore();
  const { isAuthenticated } = useAuthStore();

  const inWishlist = isInWishlist(product.id);
  const discountPercent = product.discountedPrice
    ? getDiscountPercent(product.price, product.discountedPrice)
    : null;
  const displayPrice = product.discountedPrice ?? product.price;

  const handleAddToCart = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (product.requiresPrescription) {
      toast.info('Prescription Required', 'Upload a prescription to purchase this item.');
      return;
    }
    setAddingToCart(true);
    try {
      const res = await cartService.addItem(product.id, 1);
      setCart(res.data.data);
      toast.success('Added to bag', product.name);
    } catch {
      toast.error('Could not add to bag');
    } finally {
      setAddingToCart(false);
    }
  };

  const handleWishlist = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!isAuthenticated) { toast.info('Please login', 'Sign in to save items.'); return; }
    try {
      if (inWishlist) {
        await api.delete(`/wishlist/items/${product.id}`);
        removeItem(product.id);
      } else {
        const res = await api.post('/wishlist/items', { productId: product.id });
        addItem(res.data.data);
        toast.success('Saved');
      }
    } catch { toast.error('Could not update saved items'); }
  };

  return (
    <div className={cn(
      'group relative bg-cream-2 rounded-card border border-rule/50 shadow-soft transition-all duration-300 hover:shadow-card-hover hover:-translate-y-0.5 overflow-hidden',
      className
    )}>
      <Link to={`/products/${product.slug}`}>
        {/* Image */}
        <div className="relative bg-cream h-44 overflow-hidden">
          <img
            src={product.images[0] || '/placeholder-product.jpg'}
            alt={product.name}
            className="w-full h-full object-contain p-4 transition-transform duration-500 group-hover:scale-[1.04]"
            loading="lazy"
          />
          {discountPercent && (
            <span className="tag-discount absolute top-2.5 right-2.5">-{discountPercent}%</span>
          )}
          {product.requiresPrescription && (
            <span className="tag-rx absolute top-2.5 left-2.5">Rx</span>
          )}
          {product.stock === 0 && (
            <div className="absolute inset-0 bg-cream/70 backdrop-blur-[1px] flex items-center justify-center">
              <span className="text-xs font-semibold text-muted px-3 py-1 bg-cream rounded-pill border border-rule">Out of Stock</span>
            </div>
          )}
          <button
            onClick={handleWishlist}
            className={cn(
              'absolute bottom-2.5 right-2.5 w-7 h-7 rounded-full flex items-center justify-center transition-all duration-200',
              'opacity-0 group-hover:opacity-100',
              inWishlist ? 'bg-cream text-accent-500' : 'bg-cream/80 text-muted hover:text-accent-500'
            )}
          >
            <Heart className="h-3.5 w-3.5" fill={inWishlist ? 'currentColor' : 'none'} />
          </button>
        </div>

        {/* Info */}
        <div className="px-4 pt-3.5 pb-3">
          {product.brand && (
            <p className="kicker text-[10px] mb-1 truncate">{product.brand.name}</p>
          )}
          <h3 className="text-sm font-medium text-ink line-clamp-2 min-h-[2.5rem] leading-snug group-hover:text-primary-600 transition-colors duration-200">
            {product.name}
          </h3>
          {product.avgRating !== undefined && product.avgRating > 0 && (
            <div className="flex items-center gap-1.5 mt-1.5">
              <RatingStars rating={product.avgRating} size="sm" />
              <span className="text-[11px] text-muted">({product.reviewCount ?? 0})</span>
            </div>
          )}
          <div className="flex items-center gap-2 mt-2">
            <span className="num font-display font-semibold text-base text-primary-500">
              {formatCurrency(Number(displayPrice))}
            </span>
            {product.discountedPrice && (
              <span className="num text-xs text-muted line-through">{formatCurrency(Number(product.price))}</span>
            )}
          </div>
        </div>
      </Link>

      {/* Add-to-bag */}
      <div className="px-4 pb-4">
        <button
          onClick={handleAddToCart}
          disabled={product.stock === 0 || addingToCart}
          className={cn(
            'w-full flex items-center justify-center gap-1.5 py-2 rounded-pill text-xs font-semibold transition-all duration-300',
            product.requiresPrescription
              ? 'bg-cream-3 text-muted border border-rule cursor-default'
              : product.stock === 0
              ? 'bg-cream-3 text-muted cursor-not-allowed'
              : 'bg-primary-500/10 text-primary-600 hover:bg-primary-500 hover:text-white border border-primary-200 hover:border-transparent',
            addingToCart && 'opacity-60'
          )}
        >
          {addingToCart ? (
            <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          ) : (
            <ShoppingBag className="h-3.5 w-3.5" />
          )}
          {product.requiresPrescription ? 'Prescription required'
            : product.stock === 0 ? 'Out of stock'
            : 'Add to bag'}
        </button>
      </div>
    </div>
  );
};
